import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Check, Loader2, Plug, X } from "lucide-react";
import { API } from "@/lib/api";
import { useKaartToken, GRIJS } from "@/lib/kaartToken";
import OpenOpnieuw from "./OpenOpnieuw";
import { magKoppelen } from "./NextcloudConnection";
import { useTaal } from "@/contexts/TaalContext";

/** De bevestigingskaarten voor MCP-aanroepen die een agent voorstelde. I4,
 *  17 oktober 2026.
 *
 *  BESLUIT FRANK: ELKE AANROEP WACHT OP JE KLIK. Een agent stelt voor; pas
 *  Bevestig stuurt `tools/call` naar de server. Weigeren stuurt niets.
 *
 *  DE VELDEN KOMEN VAN DE SERVER. Server, gereedschap en argumenten staan er
 *  zoals ze zijn opgeslagen, en precies dat voert de server uit. Zelfde regel
 *  als bij `TaakVoorstel`: je bevestigt wat er staat, niet wat een model schreef.
 *
 *  I5: na bevestigen staat het resultaat ook in het gesprek waar de agent het
 *  voorstelde, met server en tool als bron; de kaart linkt erheen.
 *
 *  WF-40: de kaart haalt bij het tonen een token, en alleen met dat token doet
 *  Bevestig iets, een keer. Een kijker krijgt geen knoppen; de server weigert
 *  hem ook (403).
 */
export function argumentTekst(argumenten) {
  const leeg = !argumenten || Object.keys(argumenten).length === 0;
  return leeg ? "" : JSON.stringify(argumenten, null, 2);
}

/** B3 deel 1 (29 okt): per soort uit de akkoordlijst van de backend (`services/akkoordlijst.py`)
 *  de zin op de kaart. Een onbekende soort toont niets. */
export const AKKOORD = {
  versturen: "Dit verstuurt iets. Er gebeurt niets tot je bevestigt.",
  betalen: "Dit is een betaling. Er gebeurt niets tot je bevestigt.",
  ondertekenen: "Dit ondertekent of keurt iets goed. Er gebeurt niets tot je bevestigt.",
  verwijderen: "Dit verwijdert iets. Er gebeurt niets tot je bevestigt.",
};

function McpKaart({ rij: begin, rol, onBeslist }) {
  const { t } = useTaal();
  const [rij, setRij] = useState(begin);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState("");
  const mag = magKoppelen(rol);
  const open = rij.status === "wacht";
  const { token, vernieuw, vervallen, laatVervallen, reden } = useKaartToken(
    `${API}/mcp-aanroepen/${rij.id}/toon`, open && mag);

  const beslis = async (soort) => {
    setBezig(true);
    setFout("");
    try {
      const r = await axios.post(`${API}/mcp-aanroepen/${rij.id}/${soort}`,
        soort === "bevestig" ? { token } : {});
      setRij(r.data);
      onBeslist?.();
    } catch (e) {
      const tekst = e.response?.data?.detail || t("Er is niets verstuurd");
      setFout(tekst);
      if (soort === "bevestig") laatVervallen(tekst);
    } finally {
      setBezig(false);
    }
  };

  const args = argumentTekst(rij.argumenten);
  const status = {
    uitgevoerd: t("Uitgevoerd"), mislukt: t("Mislukt"), geweigerd: t("Geweigerd"),
    bezig: t("Bezig"),
    // BB2 (4 nov): een kaart die 24 uur open stond, is vervallen; er is niets verstuurd.
    vervallen: t("Vervallen, er is niets verstuurd"),
  }[rij.status];
  return (
    <div className="border rounded-lg p-3 space-y-2 bg-background" data-testid="mcp-kaart">
      <p className="text-xs font-medium">
        <Plug className="h-3 w-3 inline mr-1" />
        {rij.agent ? `${rij.agent}: ${t("wil een MCP-gereedschap aanroepen")}`
          : t("De chat wil een gereedschap van een koppeling aanroepen")}
      </p>
      <dl className="text-xs grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
        <dt className="text-[hsl(var(--muted-foreground))]">{t("Server")}</dt>
        <dd data-testid="mcp-server">{rij.server?.naam} <span className="text-[hsl(var(--muted-foreground))]">{rij.server?.adres}</span></dd>
        <dt className="text-[hsl(var(--muted-foreground))]">{t("Gereedschap")}</dt>
        <dd data-testid="mcp-tool" className="font-mono">{rij.tool}</dd>
      </dl>
      {AKKOORD[rij.akkoord] && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400" role="note" data-testid="mcp-akkoord">
          {t(AKKOORD[rij.akkoord])}
        </p>
      )}
      {/* GL220-2: leesbaar boven de JSON (productnaam, artikelnummer, aantal). De backend schrijft hem in de
          taal van de beurt, uit de uitslag waar het product vandaan kwam; leeg als die er niet was. */}
      {rij.samenvatting && (
        <p className="text-sm font-medium" data-testid="mcp-samenvatting">{rij.samenvatting}</p>
      )}
      {args ? (
        <pre data-testid="mcp-argumenten" className="text-xs bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{args}</pre>
      ) : (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">{t("Zonder argumenten")}</p>
      )}
      {open && mag ? (
        <div className="flex flex-wrap gap-2 items-center">
          <Button size="sm" data-testid="mcp-bevestig" disabled={bezig || !token}
            className={vervallen ? GRIJS : ""} onClick={() => beslis("bevestig")}>
            {bezig ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
            {t("Bevestig en verstuur")}
          </Button>
          <Button size="sm" variant="outline" data-testid="mcp-weiger" disabled={bezig}
            onClick={() => beslis("weiger")}>
            <X className="h-3 w-3 mr-1" />{t("Weiger")}
          </Button>
          {vervallen && <OpenOpnieuw onClick={vernieuw} bezig={bezig} />}
        </div>
      ) : open ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="mcp-kijker">
          {t("Alleen een eigenaar of lid kan dit bevestigen.")}
        </p>
      ) : (
        <div className="space-y-1" data-testid="mcp-uitkomst">
          <p className="text-xs font-medium">{status}</p>
          {rij.resultaat && <pre className="text-xs whitespace-pre-wrap break-words">{rij.resultaat}</pre>}
          {/* I5: het resultaat staat ook in het gesprek, met server en tool als bron. */}
          {rij.bericht_id && rij.gesprek_id && (
            <Link to={`/chat/${rij.gesprek_id}`} data-testid="mcp-naar-gesprek"
              className="text-xs underline text-[hsl(var(--accent))]">
              {t("Het resultaat staat in het gesprek")}
            </Link>
          )}
        </div>
      )}
      {(fout || reden) && <p className="text-xs text-destructive" data-testid="mcp-fout">{fout || reden}</p>}
    </div>
  );
}

export default function McpBevestigingen({ ververs, gesprekId = null, onBeslist }) {
  const { t } = useTaal();
  const [rijen, setRijen] = useState([]);
  const [rol, setRol] = useState("");

  const haal = useCallback(async () => {
    try {
      // W1v1: in een gesprek alleen de kaarten van dat gesprek.
      const params = gesprekId ? { status: "wacht", gesprek_id: gesprekId } : { status: "wacht" };
      const r = await axios.get(`${API}/mcp-aanroepen`, { params });
      setRijen(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRijen([]);
    }
  }, [gesprekId]);

  useEffect(() => { haal(); }, [haal, ververs]);
  useEffect(() => {
    axios.get(`${API}/organisatie`).then((o) => setRol(o.data?.mijn_rol || "")).catch(() => setRol(""));
  }, []);

  if (!rijen.length) return null;
  return (
    <div className="border-b px-4 py-2 space-y-2" data-testid="mcp-bevestigingen">
      <p className="text-xs font-medium text-[hsl(var(--accent))]">
        {rijen.length === 1 ? t("Een MCP-aanroep wacht op je bevestiging")
          : `${rijen.length} ${t("MCP-aanroepen wachten op je bevestiging")}`}
      </p>
      {rijen.map((r) => <McpKaart key={r.id} rij={r} rol={rol} onBeslist={onBeslist} />)}
    </div>
  );
}
